import React, { ButtonHTMLAttributes, useMemo } from 'react';
import clsx from 'clsx';
import type { AriaButtonProps } from 'react-aria';
import { useFocusRing } from 'react-aria/useFocusRing';
import { useHover } from 'react-aria/useHover';
import { useMessageFormatter } from '@react-aria/i18n';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { mergeProps } from 'react-aria/mergeProps';
import { mergeRefs } from 'react-aria/mergeRefs';
import { useFocusableRef } from '@react-spectrum/utils';
import { FocusableRef, LinkDOMProps, PressEvents } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/button/button.module.css';

import intlMessages from '../i18n';
import { Icon } from '../../Icon';
import { Spinner } from '../../Spinner';
import {
  SapphireStyleProps,
  useButton,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { TooltipIfNeeded } from '../../Table';

export type ButtonProps<T extends 'a' | 'button'> = AriaButtonProps<T> &
  Omit<LinkDOMProps, 'routerOptions'> &
  SapphireStyleProps &
  GlobalDomAttributes &
  PressEvents &
  Pick<
    ButtonHTMLAttributes<T>,
    | 'form'
    | 'formAction'
    | 'formEncType'
    | 'formMethod'
    | 'formNoValidate'
    | 'formTarget'
    | 'name'
    | 'value'
  > & {
    /**
     * @default 'md'
     */
    size?: 'lg' | 'md' | 'sm';

    /**
     * @default false
     */
    isLoading?: boolean;

    /**
     * The visual style of the button.
     * @default 'primary'
     */
    variant?: ButtonVariant;

    /**
     * @deprecated
     * Please use onPress instead, which has
     * [several UX
     *   improvements](https://react-spectrum.adobe.com/blog/building-a-button-part-1.html) over
     *   onClick. onPress* event handlers however don't give access to the native event, which can
     *   be an issue in rare cases where you need to call preventDefault for example. This is a
     *   temporary escape hatch for those cases until we can improve onPress* handlers to support
     *   those cases too.
     */
    UNSAFE_onClick?: React.MouseEventHandler;
  } & (ButtonWithIconProps | ButtonWithoutIconProps);

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'text'
  | 'danger'
  | 'danger-secondary'
  | 'danger-tertiary'
  | 'danger-text';

export interface ButtonWithIconProps {
  /**
   * The icon placed either before or after the `label`, based on `iconAlign` prop.
   * */
  icon: React.ReactNode;
  /**
   * The icon placement relative to the `label`.
   * */
  iconAlign?: 'left' | 'right';
}

export interface ButtonWithoutIconProps {
  icon?: never;
  iconAlign?: never;
}

export const Button = React.forwardRef(function Button<
  T extends 'a' | 'button'
>(
  {
    isLoading = false,
    iconAlign = 'left',
    variant = 'primary',
    icon,
    size = 'md',
    UNSAFE_onClick,
    onPress: onPressOriginal,
    ...otherProps
  }: ButtonProps<T>,
  ref: FocusableRef<HTMLButtonElement | HTMLAnchorElement>
) {
  useThemeCheck();

  const { autoFocus, isDisabled } = otherProps;

  const canClick = useMemo(
    () => !isDisabled && !isLoading,
    [isDisabled, isLoading]
  );
  const onPress = useMemo(
    () => (canClick ? onPressOriginal : () => {}),
    [canClick, onPressOriginal]
  );

  const {
    elementType: ElementType = (otherProps.href ? 'a' : 'button') as string,
  } = otherProps;
  const formatMessage = useMessageFormatter(intlMessages);
  const domRef = useFocusableRef<HTMLButtonElement | HTMLAnchorElement>(ref);
  const { buttonProps, isPressed } = useButton(
    {
      ...otherProps,
      onPress,
      elementType: ElementType as 'a' | 'button',
    },
    domRef
  );
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { focusProps, isFocusVisible } = useFocusRing({ autoFocus });
  const { styleProps } = useSapphireStyleProps(otherProps);

  const iconWrapper = icon && (
    <span className={clsx(styles['sapphire-button__icon'])} aria-hidden="true">
      {!isLoading && <Icon size={size === 'lg' ? 'md' : 'sm'}>{icon}</Icon>}
    </span>
  );

  const domProps = filterDOMProps(otherProps, {
    global: true,
    propNames: new Set([
      'form',
      'formAction',
      'formEncType',
      'formMethod',
      'formNoValidate',
      'formTarget',
      'name',
      'value',
    ]),
    isLink: true,
  });

  return (
    <TooltipIfNeeded<HTMLButtonElement | HTMLAnchorElement>
      title={otherProps.children}
    >
      {(tooltipRef, tooltipProps) => {
        return (
          <ElementType
            {...mergeProps(
              buttonProps,
              hoverProps,
              domProps,
              focusProps,
              tooltipProps,
              {
                onClick: canClick ? UNSAFE_onClick : undefined,
                tabIndex: isDisabled ? -1 : buttonProps.tabIndex || 0,
              }
            )}
            ref={mergeRefs(domRef, tooltipProps?.ref)}
            aria-disabled={!canClick}
            style={{ ...styleProps.style }}
            className={clsx(
              styles['sapphire-button'],
              styles[`sapphire-button--${variant}`],
              styles['js-focus'],
              styles['js-hover'],
              {
                [styles['is-active']]: isPressed,
                [styles['is-focus']]: isFocusVisible,
                [styles['is-hover']]: isHovered,
                [styles['is-disabled']]: isDisabled,
                [styles['sapphire-button--sm']]: size === 'sm',
                [styles['sapphire-button--lg']]: size === 'lg',
                [styles['sapphire-button--loading']]: isLoading,
              },
              styleProps.className
            )}
          >
            {isLoading && (
              <span className={clsx(styles['sapphire-button__spinner'])}>
                <Spinner
                  aria-label={formatMessage('loading')}
                  variant="inherit"
                  size={size === 'sm' ? '2xs' : size === 'md' ? 'xs' : 'sm'}
                />
              </span>
            )}
            {iconAlign === 'left' && iconWrapper}
            <span
              ref={tooltipRef}
              className={clsx(styles['sapphire-button__content'])}
              {...(isLoading ? { 'aria-hidden': true } : {})}
            >
              {otherProps.children}
            </span>
            {iconAlign === 'right' && iconWrapper}
          </ElementType>
        );
      }}
    </TooltipIfNeeded>
  );
});
